<template>
  <div class="step">
    <!-- POSTCODE & ADDRESS -->
    <component
      :is="'question-address'"
      product-name="home"
      step-id="home"
      question-id="address"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :session-data="form['home']"
      subtitle="What's the address of the house you're insuring?"
      @input="updateStepData"
    />

    <!-- PROPERTY OWNED -->
    <component
      :is="'question-property-owned'"
      product-name="home"
      step-id="home"
      question-id="property-owned"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :validation-errors="{
        propertyOwned: {
          is_not: 'Please confirm if you own or rent your home.',
        },
      }"
      @input="updateStepData"
    />

    <!-- PROPERTY COVER TYPE -->
    <component
      :is="'question-property-cover-type'"
      product-name="home"
      step-id="home"
      question-id="property-cover-type"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :help-text="{
        value: `
          <p>
            <b>Buildings and Contents cover</b> – This provides cover for both the buildings and the contents of the property.
          </p>
          <p>
            <b> Buildings Only</b> - This covers the structure of your home including the roof walls, windows, fixtures and fixings.
          </p>
          <p>
            <b>Contents Only</b> - This covers items that belong to people who live in the property. This can include clothes, electronic goods, furniture, carpets and curtains.
          </p>`,
        collapsible: isExperimentMinimiseHelptext,
        openerText: 'Insurance Cover type',
      }"
      @input="updateStepData"
    />

    <!-- PROPERTY SORT -->
    <component
      :is="'question-property-sort'"
      product-name="home"
      step-id="home"
      question-id="property-sort"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :help-text="{
        value: `
          <p>
            If you're currently renting a room in the property and share a bathroom, please
            select <b>‘Other’</b> then <b>‘Bedsit/Halls of Residence’</b>.
          </p>
          <p>
            If you live in a flat/apartment that has more than one storey, please select <b>‘Flat/Apartment’</b> then <b>‘Maisonette’</b>.
          </p>`,
        collapsible: isExperimentMinimiseHelptext,
        openerText: 'Selecting Property Type',
      }"
      @input="updateStepData"
    />

    <!-- PROPERTY TYPE -->
    <component
      :is="'question-property-type'"
      product-name="home"
      step-id="home"
      question-id="property-type"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :validation-errors="{
        propertyType: {
          required: 'Please select your property type.',
        },
      }"
      @input="updateStepData"
    />

    <!--  TODO: replace with common component  -->
    <!-- PROPERTY FLOOR -->
    <component
      :is="'question-property-floor'"
      product-name="home"
      step-id="home"
      question-id="property-floor"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :validation-errors="{
        propertyFloor: {
          required: 'Please select your property\'s floor.',
        },
      }"
      @input="updateStepData"
    />

    <component
      :is="'question-base-counter-list'"
      product-name="home"
      step-id="home"
      question-id="property-rooms"
      :form-observer="formObserver"
      title="How many rooms does the property have?"
      :help-text="propertyRoomsHelpText"
      :session-data="form['home']"
      :custom-fields="[
        'propertyBedrooms',
        'propertyBathrooms',
        'propertyLivingrooms',
        'propertyOtherooms',
      ]"
      :options-config="{
        options: {
          propertyBedrooms: {
            label: 'Bedrooms',
            max: 99,
            min: 1,
          },
          propertyBathrooms: {
            label: 'Bathrooms',
            max: 99,
            min: 1
          },
          propertyLivingrooms: {
            label: 'Living room/lounges',
            max: 99,
          },
          propertyOtherooms: {
            label: 'Other rooms',
            max: 99,
          }
        }
      }"
      :form-data="form"
      :step-data="step"
      @toast="showToast"
      @input="updateStepData"
    />

    <!-- PROPERTY YEAR -->
    <component
      :is="'question-property-year'"
      product-name="home"
      step-id="home"
      question-id="property-year"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      @input="updateStepData"
    />

    <!-- PROPERTY ROOF MADE -->
    <component
      :is="'question-property-roof'"
      product-name="home"
      step-id="home"
      question-id="property-roof-made"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :validation-errors="{
        propertyRoofMade: {
          is_not: 'Please select the roof material.',
        },
      }"
      :help-text="{
        value: `
          <p>Please choose the material that the majority of the roof is made from.</p>
          <p>Slate is a natural stone product. Tiles will generally be made from concrete or clay and come in a variety of profiles, shapes and colours.</p>
        `,
        collapsible: isExperimentMinimiseHelptext,
        openerText: 'Selecting Roof Material',
      }"
      @input="updateStepData"
    />

    <!-- PROPERTY ROOF FLAT -->
    <component
      :is="'question-property-flat-roof'"
      product-name="home"
      step-id="home"
      question-id="property-flat-roof"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      @input="updateStepData"
    />

    <!-- PROPERTY ASSUMPTIONS -->
    <component
      :is="'question-property-assumptions'"
      product-name="home"
      step-id="home"
      question-id="property-assumptions"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      @input="updateStepData"
    />

    <!-- BUILD SUMS INS -->
    <component
      :is="'question-build-sums-ins'"
      product-name="home"
      step-id="home"
      question-id="build-sums-ins"
      title="How much buildings cover do you need?"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :help-text="{
        value: '<p>Note, this is different to the market value of the home – usually a higher value as it includes the land value your home sits on.</p><p class=\'pt-0\'>If unsure, you may wish to obtain professional advice. If your home is found to be underinsured in the event of a claim, your insurer may not settle the full claim.</p>',
        title: 'This is the estimated cost of rebuilding your home. You can usually find this figure in your mortgage valuation documents, surveyors report or previous insurance documents.',
        openerText: 'Show more',
        closerText: 'Hide',
        collapsible: true,
        fullWidth: true
      }"
      :prerequisites="[
        {
          field: 'postcode',
          validationRules: {
            required: true
          },
        },
        {
          field: 'houseNumberOrName',
          validationRules: {
            required: true
          },
        },
        {
          field: 'propertyType',
          validationRules: {
            required: true
          },
        },
        {
          field: 'propertySort',
          validationRules: {
            required: true
          },
        },
        {
          field: 'propertyWallsMade',
          validationRules: {
            required: true
          },
        },
        {
          field: 'propertyYear',
          validationRules: {
            propertyYear: true,
            required: true,
          },
        },
        {
          field: 'propertyCoverType',
          validationRules: {
            is_not: {
              other: 'C',
            },
            required: true,
          },
        },
        {
          field: 'propertyRoofMade',
          validationRules: {
            required: true
          },
        },
        { field: 'propertyBedrooms' },
        { field: 'propertyBathrooms' },
      ]"
      :custom-fields="{ field: 'buildSumsIns' }"
      :show-rules="{
        propertyCoverType: {
          is_not: {
            other: 'C'
          },
          required: true
        }
      }"
      @toast="showToast"
      @input="updateStepData"
    />
  </div>
</template>

<script>
import QuestionPropertyOwned from '@/components/questions/home/PropertyOwned';
import QuestionPropertyCoverType from '@/components/questions/home/PropertyCoverType';
import QuestionPropertySort from '@/components/questions/home/PropertySort';
import QuestionPropertyType from '@/components/questions/home/PropertyType';
import QuestionPropertyFloor from '@/components/questions/home/PropertyFloor';
import QuestionPropertyYear from '@/components/questions/home/PropertyYear';
import QuestionPropertyRoof from '@/components/questions/home/PropertyRoof';
import QuestionPropertyFlatRoof from '@/components/questions/home/PropertyFlatRoof';
import QuestionPropertyAssumptions from '@/components/questions/home/PropertyAssumptions';
import QuestionBuildSumsIns from '@/components/questions/home/BuildSumsIns';

import { mapActions } from 'vuex';

import {
  cloneDeep,
} from 'lodash';
import useExperimentMinimiseHelptext from '@/composables/useExperimentMinimiseHelptext';

export default {
  name: 'form-home-step-1',
  components: {
    QuestionPropertyOwned,
    QuestionPropertyCoverType,
    QuestionPropertySort,
    QuestionPropertyType,
    QuestionPropertyFloor,
    QuestionPropertyYear,
    QuestionPropertyRoof,
    QuestionPropertyFlatRoof,
    QuestionPropertyAssumptions,
    QuestionBuildSumsIns,
  },
  inject: [
    'config',
    'inputCharsLimiters',
    'constants',
  ],
  props: {
    form: {
      type: Object,
      required: true,
    },
    formObserver: {
      type: Object,
      required: true,
    },
  },
  data () {
    const propertyRoomsHelpText = [
      '<p><b style="font-size: 18px;">Bedrooms</b><p><b>Include</b> any rooms that were originally built as bedrooms and any that have been converted for another use, e.g. an office or study.</p></p>',
      '<p><b style="font-size: 18px;">Bathrooms</b><p><b>Include</b> all rooms containing a toilet, including en-suites and shower rooms.</p></p>',
      '<p><b style="font-size: 18px;">Living room/lounges</b><p><b>Include</b> any reception rooms such as lounges, dining rooms or sitting rooms.</p></p>',
      '<p><b style="font-size: 18px;">Other rooms</b><p><b>Exclude</b> kitchens, hallways, landing, utility rooms, conservatories and any rooms not attached to your house. You do not need to add a kitchen as insurers presume every house has one.</p></p>',
    ];

    return {
      step: null,
      propertyRoomsHelpText,
    };
  },
  watch: {
    step: {
      handler (val) {
        this.$emit('step-data', val);
      },
      immediate: true,
    },
  },
  async created () {
    await this.initStep();
  },
  methods: {
    ...mapActions(['showToast']),
    async initStep () {
      this.$emit('tracking', 'home');

      this.step = cloneDeep(this.form.home);

      this.formObserver.reset();
    },
    updateStepData (questionData) {
      this.step = { ...this.step, ...questionData };
    },
  },
  setup () {
    // COMPOSABLES
    const { isExperimentMinimiseHelptext } = useExperimentMinimiseHelptext();

    return {
      isExperimentMinimiseHelptext,
    };
  },
};
</script>
